import React from 'react';
import clsx from 'clsx';
import { PencilSquareIcon } from '@heroicons/react/24/outline';
import type { DecisaoImportacaoGlpi, ItemPreviaImportacaoGlpi } from '../../../../services';
import type { StatusCustodiaEquipamento } from '../../../../models';
import { TIPO_ATIVO_LABELS } from './ativos.definitions';
import { descreverSituacao, ROTULO_CAMPO_ATIVO } from './ativoHistorico.format';
import type { ContextoAtivos } from './ativos.utils';
import { normalizarPatrimonio } from '../../../../models/equipamento.utils';

export const ACAO_LABELS: Record<ItemPreviaImportacaoGlpi['acao'], string> = {
  novo: 'Novo',
  completar: 'Completar',
  sem_alteracao: 'Sem alteração',
  conflito: 'Conflito',
  excluido: 'Excluído no Digital',
};

export const ACAO_BADGE: Record<ItemPreviaImportacaoGlpi['acao'], string> = {
  novo: 'bg-green-100 text-green-800',
  completar: 'bg-blue-100 text-blue-800',
  sem_alteracao: 'bg-gray-100 text-gray-600',
  conflito: 'bg-red-100 text-red-700',
  excluido: 'bg-gray-200 text-gray-500 line-through',
};

export type DecisaoLocal = Omit<DecisaoImportacaoGlpi, 'chave'>;

/** Item + ajustes feitos na tela = como vai ficar (pra exibir). */
export function valorFinal(item: ItemPreviaImportacaoGlpi, decisao: DecisaoLocal | undefined, campo: string): string | null {
  if (decisao?.ajustes && campo in decisao.ajustes) return decisao.ajustes[campo] ?? null;
  return ((item as unknown as Record<string, unknown>)[campo] as string | null) ?? null;
}

/** Avisos que ainda valem (os resolvidos por um ajuste somem). */
export function avisosEfetivos(item: ItemPreviaImportacaoGlpi, decisao: DecisaoLocal | undefined) {
  return item.avisos.filter((a) => !(decisao?.ajustes && a.campo in decisao.ajustes));
}

export type FiltroPendencia = 'todas' | 'usuario' | 'cliente' | 'projeto' | 'situacao' | 'divergencia' | 'nenhuma';

export const FILTROS_PENDENCIA: Array<{ valor: FiltroPendencia; label: string }> = [
  { valor: 'todas', label: 'Todas' },
  { valor: 'usuario', label: 'Usuário' },
  { valor: 'cliente', label: 'Cliente' },
  { valor: 'projeto', label: 'Projeto' },
  { valor: 'situacao', label: 'Situação' },
  { valor: 'divergencia', label: 'Divergência' },
  { valor: 'nenhuma', label: 'Sem pendência' },
];

const CAMPO_DO_FILTRO: Partial<Record<FiltroPendencia, string>> = {
  usuario: 'colaboradorAtualId',
  cliente: 'clienteProprietarioId',
  projeto: 'projetoClienteId',
  situacao: 'statusCustodia',
};

/** O item cai no filtro de pendência? (considera os ajustes já feitos na tela). */
export function atendeFiltroPendencia(item: ItemPreviaImportacaoGlpi, decisao: DecisaoLocal | undefined, filtro: FiltroPendencia): boolean {
  if (filtro === 'todas') return true;
  const avisos = avisosEfetivos(item, decisao);
  if (filtro === 'divergencia') return item.divergencias.length > 0;
  if (filtro === 'nenhuma') return avisos.length === 0 && item.divergencias.length === 0 && item.acao !== 'conflito';
  return avisos.some((a) => a.campo === CAMPO_DO_FILTRO[filtro]);
}

/** Pode ser marcado pra importar: novo/completar sempre; conflito só depois de resolvido. */
export function podeImportar(item: ItemPreviaImportacaoGlpi, decisao: DecisaoLocal | undefined): boolean {
  // Excluído no Digital nunca é recriado pela importação — restaure em "Excluídos" se foi engano.
  if (item.acao === 'excluido') return false;
  if (item.acao === 'novo' || item.acao === 'completar') return true;
  if (item.acao === 'conflito') return Boolean(decisao?.resolucao) && !item.motivoConflito?.includes('ligado a outro item do GLPI');
  return Boolean(decisao?.ajustes && Object.keys(decisao.ajustes).length > 0);
}

interface ImportarGlpiLinhaProps {
  item: ItemPreviaImportacaoGlpi;
  decisao: DecisaoLocal | undefined;
  onDecisao: (decisao: DecisaoLocal) => void;
  marcado: boolean;
  onAlternar: () => void;
  onAjustar: () => void;
  contexto: ContextoAtivos;
}

/** Valor legível de um campo (ids de colaborador/cliente viram nome). */
function legivel(campo: string, valor: string, contexto: ContextoAtivos): string {
  if (campo === 'colaboradorAtualId') return contexto.nomeColaboradorPorId.get(valor) ?? valor;
  if (campo === 'projetoClienteId' || campo === 'clienteProprietarioId') return contexto.nomeClientePorId.get(valor) ?? valor;
  if (campo === 'tipo') return TIPO_ATIVO_LABELS[valor] ?? valor;
  return valor;
}

/**
 * Uma linha da revisão: o que vem do GLPI, como vai ficar no Digital e o que precisa de
 * decisão — divergências (Digital × GLPI, fica o do Digital por padrão), conflito (escolher a
 * resolução) e avisos (somem quando o campo é ajustado em "Ajustar").
 */
export default function ImportarGlpiLinha({ item, decisao, onDecisao, marcado, onAlternar, onAjustar, contexto }: ImportarGlpiLinhaProps) {
  const selecionavel = podeImportar(item, decisao);
  const ajustado = Boolean(decisao?.ajustes && Object.keys(decisao.ajustes).length > 0);
  const colaboradorId = valorFinal(item, decisao, 'colaboradorAtualId');
  const projetoId = valorFinal(item, decisao, 'projetoClienteId');
  const status = valorFinal(item, decisao, 'statusCustodia');
  const situacao = status
    ? descreverSituacao(status as StatusCustodiaEquipamento, valorFinal(item, decisao, 'situacaoDetalhe'))
    : 'Em estoque (sem situação no GLPI)';
  const avisos = avisosEfetivos(item, decisao);
  const usarGlpi = new Set(decisao?.usarGlpi ?? []);
  // Cadastro do Digital já ligado a OUTRO item do GLPI: nenhuma das resoluções serve.
  const ligadoAOutroGlpi = Boolean(item.motivoConflito?.includes("ligado a outro item do GLPI"));

  const alternarUsarGlpi = (campo: string) => {
    const novo = new Set(usarGlpi);
    if (novo.has(campo)) novo.delete(campo);
    else novo.add(campo);
    onDecisao({ ...decisao, usarGlpi: Array.from(novo) });
  };

  return (
    <li className={clsx('flex gap-3 px-3 py-2 text-sm', !selecionavel && 'bg-gray-50')}>
      <input
        type="checkbox"
        className="mt-1 h-4 w-4 rounded border-gray-300 text-indigo-600 disabled:opacity-40"
        checked={marcado && selecionavel}
        disabled={!selecionavel}
        onChange={onAlternar}
        title={selecionavel ? undefined : item.acao === 'conflito' ? 'Escolha como resolver o conflito' : 'Nada a importar'}
      />
      <div className="min-w-0 flex-1 space-y-0.5">
        <p className="flex flex-wrap items-center gap-x-2 text-gray-900">
          <span className={clsx('rounded-full px-2 py-0.5 text-xs font-semibold', ACAO_BADGE[item.acao])}>{ACAO_LABELS[item.acao]}</span>
          {ajustado && <span className="rounded-full bg-indigo-100 px-2 py-0.5 text-xs font-semibold text-indigo-700">Ajustado</span>}
          <span className="font-semibold">{valorFinal(item, decisao, 'patrimonio') ?? 'Sem patrimônio'}</span>
          <span className="text-gray-500">
            {TIPO_ATIVO_LABELS[valorFinal(item, decisao, 'tipo') ?? ''] ?? item.tipo}
            {valorFinal(item, decisao, 'modelo') ? ` · ${valorFinal(item, decisao, 'modelo')}` : ''}
            {valorFinal(item, decisao, 'nSerie') ? ` · ${valorFinal(item, decisao, 'nSerie')}` : ''}
          </span>
          <button type="button" onClick={onAjustar} className="ml-auto flex items-center gap-1 text-xs font-medium text-blue-600 hover:underline">
            <PencilSquareIcon className="h-3.5 w-3.5" />
            Ajustar
          </button>
        </p>
        <p className="text-xs text-gray-600">
          <span className="text-gray-400">{item.statusGlpi ?? 'sem status'}</span> → {item.acao === 'novo' ? situacao : 'Situação do Digital mantida'}
          {projetoId ? ` · Projeto: ${contexto.nomeClientePorId.get(projetoId) ?? projetoId}` : ''}
          {item.usuarioGlpi ? ` · Usuário GLPI: ${item.usuarioGlpi}` : ''}
          {colaboradorId ? ` → ${contexto.nomeColaboradorPorId.get(colaboradorId) ?? 'colaborador'}` : ''}
        </p>
        {item.camposACompletar.length > 0 && (
          <p className="text-xs text-blue-700">Vai completar: {item.camposACompletar.map((c) => ROTULO_CAMPO_ATIVO[c] ?? c).join(', ')}</p>
        )}
        {item.divergencias.length > 0 && (
          <div className="rounded bg-amber-50 px-2 py-1 text-xs text-amber-900">
            <p className="font-medium">Diferente no Digital e no GLPI (fica o do Digital, a não ser que marque):</p>
            {item.divergencias.map((d) => (
              <label key={d.campo} className="mt-0.5 flex items-center gap-2">
                <input type="checkbox" className="h-3.5 w-3.5 rounded border-gray-300" checked={usarGlpi.has(d.campo)} onChange={() => alternarUsarGlpi(d.campo)} />
                <span>
                  {ROTULO_CAMPO_ATIVO[d.campo] ?? d.campo}: Digital <strong>{legivel(d.campo, d.digital, contexto)}</strong> · GLPI{' '}
                  <strong>{legivel(d.campo, d.glpi, contexto)}</strong> — usar o do GLPI
                </span>
              </label>
            ))}
          </div>
        )}
        {item.acao === 'conflito' && (
          <div className="rounded bg-red-50 px-2 py-1 text-xs text-red-800">
            <p>{item.motivoConflito}</p>
            {ligadoAOutroGlpi ? (
              <p className="mt-1">Não dá pra resolver aqui: confira na ficha do ativo a qual item do GLPI ele corresponde.</p>
            ) : (
            <select
              className="mt-1 rounded border border-red-200 bg-white px-1 py-0.5 text-xs"
              value={decisao?.resolucao ?? ''}
              onChange={(e) => onDecisao({ ...decisao, resolucao: e.target.value || null })}
            >
              <option value="">Como resolver?</option>
              {item.candidatos.length > 1 ? (
                item.candidatos.map((c) => (
                  <option key={c.id} value={`ligar:${c.id}`}>
                    Ligar ao cadastro {normalizarPatrimonio(c.patrimonio) ?? 'sem patrimônio'} {c.nSerie ? `(série ${c.nSerie})` : ''}
                  </option>
                ))
              ) : (
                <>
                  <option value="manter_digital">É o mesmo equipamento — manter a série do Digital</option>
                  <option value="usar_glpi">É o mesmo equipamento — usar a série do GLPI</option>
                </>
              )}
            </select>
            )}
          </div>
        )}
        {avisos.map((aviso) => (
          <p key={aviso.mensagem} className="text-xs text-amber-700">
            ⚠ {aviso.mensagem}
          </p>
        ))}
      </div>
    </li>
  );
}
